import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Check,
  X,
  ClipboardCheck,
  PackageCheck,
  AlertTriangle,
  Zap,
  Printer,
  Store,
  RefreshCw,
  History,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const gkey = (sid, dd) => `${sid}__${dd}`;
const fmtQty = (n) => {
  const v = Number(n || 0);
  return v % 1 === 0 ? v.toFixed(0) : v.toFixed(2);
};

// 実質的な変更があるかを判定（数量・単位が同じ「空の申請」を除外するため）
const isRealChange = (it) => {
  if (it.action === "add") return Number(it.new_quantity || 0) > 0;
  if (it.action === "delete") return true;
  return (
    Number(it.old_quantity || 0) !== Number(it.new_quantity || 0) ||
    (it.old_unit || "") !== (it.new_unit || "")
  );
};

export default function ActionCenter() {
  const [groups, setGroups] = useState([]);
  const [batchRequests, setBatchRequests] = useState([]);
  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const [tab, setTab] = useState("all"); // all | approve | confirm

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const canApprove = user.role === "admin";

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [groupsRes, batchRes, prodRes, storesRes] = await Promise.all([
        axios.get(`${API}/admin/order-groups`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/orders/change-requests/batch`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/products`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/stores`, getAuthHeaders()).catch(() => ({ data: [] })),
      ]);
      setGroups(Array.isArray(groupsRes.data) ? groupsRes.data : []);
      setBatchRequests(Array.isArray(batchRes.data) ? batchRes.data : []);
      setProducts(Array.isArray(prodRes.data) ? prodRes.data : []);
      setStores(Array.isArray(storesRes.data) ? storesRes.data : []);
    } catch (e) {
      toast.error("データの取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const productsMap = useMemo(() => {
    const m = {};
    products.forEach((p) => (m[p.id] = p));
    return m;
  }, [products]);
  const storesMap = useMemo(() => {
    const m = {};
    stores.forEach((s) => (m[s.id] = s));
    return m;
  }, [stores]);

  // 変更承認: pending の一括変更申請を 店舗×配送日 でまとめる
  const pendingBatchByKey = useMemo(() => {
    const m = {};
    batchRequests.forEach((b) => {
      const realPendingItems = (b.items || []).filter(
        (i) => (i.status || "pending") === "pending" && isRealChange(i)
      );
      if (realPendingItems.length === 0) return;
      const k = gkey(b.store_id, b.delivery_date);
      (m[k] = m[k] || []).push(b);
    });
    return m;
  }, [batchRequests]);

  const groupByKey = useMemo(() => {
    const m = {};
    groups.forEach((g) => (m[gkey(g.store_id, g.delivery_date)] = g));
    return m;
  }, [groups]);

  // 発注グループが存在しない申請（全て「追加」等）でも締切判定できるよう、
  // バックエンドが付与した deadline_passed を 店舗×配送日 単位で保持する
  const batchDeadlinePassedByKey = useMemo(() => {
    const m = {};
    batchRequests.forEach((b) => {
      const k = gkey(b.store_id, b.delivery_date);
      if (b.deadline_passed) m[k] = true;
    });
    return m;
  }, [batchRequests]);

  // 要対応カード一覧
  const cards = useMemo(() => {
    const keys = new Set();
    // 締切前は閲覧のみ: 締切を過ぎた配送分のみ要対応に載せる（承認・確定とも）
    Object.keys(pendingBatchByKey).forEach((k) => {
      if (groupByKey[k]?.deadline_passed || batchDeadlinePassedByKey[k]) keys.add(k);
    });
    groups.forEach((g) => {
      if (g.deadline_passed && (!g.confirmed || g.changed)) keys.add(gkey(g.store_id, g.delivery_date));
      // 事前取込対象(魚類など)は締切前でも要対応に載せる（魚明細が未確定/変更ありの間だけ）
      if (g.early_pull_pending && !g.deadline_passed)
        keys.add(gkey(g.store_id, g.delivery_date));
    });
    const list = [...keys].map((k) => {
      const [store_id, delivery_date] = k.split("__");
      const group = groupByKey[k];
      const batches = pendingBatchByKey[k] || [];
      const store = storesMap[store_id] || {};
      const needApprove = batches.length > 0 && (!!group?.deadline_passed || !!batchDeadlinePassedByKey[k]);
      const needConfirm = !!group && group.deadline_passed && (!group.confirmed || group.changed);
      const needEarlyPull = !!group && group.early_pull_pending && !group.deadline_passed;
      const isChanged = !!group && group.changed;
      return {
        key: k,
        store_id,
        delivery_date,
        store_name: group?.store_name || store.name || store_id,
        store_code: group?.store_code || store.code || "",
        group,
        batches,
        needApprove,
        needConfirm,
        needEarlyPull,
        isChanged,
      };
    });
    // フィルタ
    const filtered = list.filter((c) => {
      if (tab === "approve") return c.needApprove;
      if (tab === "confirm") return c.needConfirm;
      if (tab === "earlypull") return c.needEarlyPull;
      return c.needApprove || c.needConfirm || c.needEarlyPull;
    });
    filtered.sort((a, b) => {
      // 事前取込(魚類)を最優先 → 変更承認あり → 配送日 → 店舗コード
      if (a.needEarlyPull !== b.needEarlyPull) return a.needEarlyPull ? -1 : 1;
      if (a.needApprove !== b.needApprove) return a.needApprove ? -1 : 1;
      if ((a.delivery_date || "") !== (b.delivery_date || ""))
        return (a.delivery_date || "").localeCompare(b.delivery_date || "");
      return (a.store_code || "").localeCompare(b.store_code || "");
    });
    return filtered;
  }, [pendingBatchByKey, groupByKey, groups, storesMap, tab, batchDeadlinePassedByKey]);

  const counts = useMemo(() => {
    let approve = 0;
    let confirm = 0;
    let changed = 0;
    let earlyPull = 0;
    Object.entries(pendingBatchByKey).forEach(([k, arr]) => {
      if (groupByKey[k]?.deadline_passed || batchDeadlinePassedByKey[k]) approve += arr.length;
    });
    groups.forEach((g) => {
      if (g.deadline_passed && !g.confirmed) confirm += 1;
      if (g.deadline_passed && g.changed) changed += 1;
      if (g.early_pull_pending && !g.deadline_passed) earlyPull += 1;
    });
    // 要対応の配送分（店舗×配送日）の総数（フィルタ非依存）
    const keys = new Set();
    Object.keys(pendingBatchByKey).forEach((k) => {
      if (groupByKey[k]?.deadline_passed || batchDeadlinePassedByKey[k]) keys.add(k);
    });
    groups.forEach((g) => {
      if (g.deadline_passed && (!g.confirmed || g.changed)) keys.add(gkey(g.store_id, g.delivery_date));
      if (g.early_pull_pending && !g.deadline_passed) keys.add(gkey(g.store_id, g.delivery_date));
    });
    return { approve, confirm, changed, earlyPull, total: keys.size };
  }, [pendingBatchByKey, groups, groupByKey, batchDeadlinePassedByKey]);

  // ── アクション ──
  const doConfirm = async (store_id, delivery_date, early = false) => {
    await axios.put(
      `${API}/admin/order-groups/confirm`,
      { store_id, delivery_date, confirmed: true, early_pull: early },
      getAuthHeaders()
    );
  };
  const doApproveBatch = async (id, comment = "") => {
    await axios.put(
      `${API}/orders/change-requests/batch/${id}/approve`,
      { comment },
      getAuthHeaders()
    );
  };
  const doRejectBatch = async (id, comment = "") => {
    await axios.put(
      `${API}/orders/change-requests/batch/${id}/reject`,
      { comment },
      getAuthHeaders()
    );
  };

  const handleConfirm = async (card) => {
    setBusyKey(card.key);
    try {
      await doConfirm(card.store_id, card.delivery_date, card.needEarlyPull);
      toast.success(card.isChanged ? "再確定しました" : "確定しました");
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "確定に失敗しました");
    } finally {
      setBusyKey(null);
    }
  };

  const handleEarlyPull = async (card) => {
    if (!window.confirm(
      `【事前取込】${card.store_name}（${card.delivery_date}）\n\n` +
      `締切前ですが、この発注を確定してやさまるデスクへ先に取り込みます（魚類などの手配用）。\n` +
      `締切前にお客様が数量変更・キャンセルした場合も、デスクへ自動で反映されます。\n\n実行しますか？`
    )) return;
    setBusyKey(card.key);
    try {
      await doConfirm(card.store_id, card.delivery_date, true);
      toast.success("事前取込（確定）しました");
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "事前取込に失敗しました");
    } finally {
      setBusyKey(null);
    }
  };

  const handleApprove = async (batchId) => {
    setBusyKey(batchId);
    try {
      await doApproveBatch(batchId);
      toast.success("変更を承認しました");
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "承認に失敗しました");
    } finally {
      setBusyKey(null);
    }
  };

  const handleReject = async (batchId) => {
    const comment = window.prompt("却下理由（任意）を入力してください", "");
    if (comment === null) return;
    setBusyKey(batchId);
    try {
      await doRejectBatch(batchId, comment || "");
      toast.success("変更を却下しました");
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "却下に失敗しました");
    } finally {
      setBusyKey(null);
    }
  };

  const handleAll = async (card) => {
    const msg = [];
    if (card.needApprove) msg.push(`変更承認 ${card.batches.length}件`);
    if (card.needConfirm) msg.push(card.isChanged ? "再確定" : "確定");
    if (!window.confirm(`${card.store_name}（${card.delivery_date}）を まとめて対応します。\n・${msg.join("\n・")}\nよろしいですか？`)) return;
    setBusyKey(card.key);
    try {
      for (const b of card.batches) {
        await doApproveBatch(b.id);
      }
      // 承認で自動確定されるが、変更ありや未確定分も確実に確定
      await doConfirm(card.store_id, card.delivery_date);
      toast.success("まとめて対応しました");
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "処理に失敗しました");
    } finally {
      setBusyKey(null);
    }
  };

  const actionLabel = (it) => {
    const p = productsMap[it.product_id];
    const name = p?.name || it.product_name || "-";
    const change =
      it.action === "modify"
        ? `${fmtQty(it.old_quantity)} ${it.old_unit || ""} → ${fmtQty(it.new_quantity)} ${it.new_unit || ""}`
        : it.action === "add"
        ? `＋ ${fmtQty(it.new_quantity)} ${it.new_unit || ""}`
        : `− ${fmtQty(it.old_quantity)} ${it.old_unit || ""}`;
    const label = it.action === "modify" ? "変更" : it.action === "add" ? (it.is_new_line ? "締切後発注" : "追加") : "削除";
    return { name, change, label };
  };

  return (
    <div className="space-y-6" data-testid="action-center">
      {/* ヘッダー */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-slate-800 flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5 text-emerald-600" />
            要対応ボックス
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            やさまるデスクへ送るために「注文確定」または「変更承認」が必要なものを、店舗×配送日ごとにまとめました。
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/admin/change-requests"
            data-testid="ac-history-link"
            className="inline-flex items-center gap-1 text-sm px-3 h-9 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-100"
          >
            <History className="h-4 w-4" />
            変更申請の履歴
          </Link>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            data-testid="ac-refresh"
            className="rounded-lg h-9"
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            更新
          </Button>
        </div>
      </div>

      {/* サマリ + フィルタ */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <button
          data-testid="ac-tab-all"
          onClick={() => setTab("all")}
          className={`text-left rounded-2xl border p-4 transition-all ${
            tab === "all" ? "border-slate-800 bg-slate-50 ring-1 ring-slate-800" : "border-slate-200 bg-white hover:border-slate-300"
          }`}
        >
          <div className="text-xs text-slate-500">要対応の配送分</div>
          <div className="text-3xl font-bold tabular-nums text-slate-800 mt-1" data-testid="ac-count-all">
            {counts.total}
          </div>
        </button>
        <button
          data-testid="ac-tab-earlypull"
          onClick={() => setTab("earlypull")}
          className={`text-left rounded-2xl border p-4 transition-all ${
            tab === "earlypull" ? "border-sky-600 bg-sky-50 ring-1 ring-sky-500" : "border-sky-200 bg-white hover:border-sky-300"
          }`}
        >
          <div className="text-xs text-sky-700 flex items-center gap-1">
            🐟 事前取込
          </div>
          <div className="text-3xl font-bold tabular-nums text-sky-700 mt-1" data-testid="ac-count-earlypull">
            {counts.earlyPull}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">魚類など・締切前の先取込</div>
        </button>
        <button
          data-testid="ac-tab-approve"
          onClick={() => setTab("approve")}
          className={`text-left rounded-2xl border p-4 transition-all ${
            tab === "approve" ? "border-amber-500 bg-amber-50 ring-1 ring-amber-400" : "border-amber-200 bg-white hover:border-amber-300"
          }`}
        >
          <div className="text-xs text-amber-700 flex items-center gap-1">
            <AlertTriangle className="h-3.5 w-3.5" /> 変更承認
          </div>
          <div className="text-3xl font-bold tabular-nums text-amber-700 mt-1" data-testid="ac-count-approve">
            {counts.approve}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">締切後の変更申請</div>
        </button>
        <button
          data-testid="ac-tab-confirm"
          onClick={() => setTab("confirm")}
          className={`text-left rounded-2xl border p-4 transition-all ${
            tab === "confirm" ? "border-sky-500 bg-sky-50 ring-1 ring-sky-400" : "border-sky-200 bg-white hover:border-sky-300"
          }`}
        >
          <div className="text-xs text-sky-700 flex items-center gap-1">
            <PackageCheck className="h-3.5 w-3.5" /> 注文確定
          </div>
          <div className="text-3xl font-bold tabular-nums text-sky-700 mt-1" data-testid="ac-count-confirm">
            {counts.confirm}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">確定待ち（うち変更あり {counts.changed}）</div>
        </button>
      </div>

      {loading ? (
        <div className="text-center py-16 text-slate-500">読み込み中...</div>
      ) : cards.length === 0 ? (
        <div className="text-center py-16 border border-slate-200 rounded-2xl bg-white">
          <PackageCheck className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
          <div className="text-slate-700 font-medium">対応が必要なものはありません</div>
          <div className="text-sm text-slate-500 mt-1">すべて確定・承認済みです。</div>
        </div>
      ) : (
        <div className="space-y-4">
          {cards.map((card) => (
            <div
              key={card.key}
              data-testid={`ac-card-${card.store_id}-${card.delivery_date}`}
              className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden"
            >
              {/* カードヘッダー */}
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <Store className="h-4 w-4 text-slate-500" />
                  <span className="font-semibold text-slate-800">{card.store_name}</span>
                  {card.store_code && (
                    <span className="text-xs text-slate-400 font-mono">({card.store_code})</span>
                  )}
                  <span className="text-slate-400">/</span>
                  <span className="text-sm text-slate-500">配送日</span>
                  <span className="font-mono text-slate-700 text-sm">{card.delivery_date}</span>
                  {card.needEarlyPull && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-sky-100 text-sky-800 border border-sky-300 animate-pulse">
                      🐟 事前取込（締切前）
                    </span>
                  )}
                  {card.needApprove && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
                      🟠 変更承認 {card.batches.reduce((s, b) => s + (b.items || []).filter((i) => (i.status || "pending") === "pending" && isRealChange(i)).length, 0)}件
                    </span>
                  )}
                  {card.needConfirm && (
                    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${card.isChanged ? "bg-red-100 text-red-700 border-red-200" : "bg-sky-100 text-sky-700 border-sky-200"}`}>
                      🔵 {card.isChanged ? "再確定待ち（変更あり）" : "確定待ち"}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Link
                    to={`/admin/print/picking-list?date=${card.delivery_date}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs px-2 py-1.5 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-100"
                  >
                    <Printer className="h-3.5 w-3.5" />
                    印刷
                  </Link>
                  {canApprove && card.needEarlyPull && !card.needApprove && !card.needConfirm ? (
                    <Button
                      size="sm"
                      onClick={() => handleEarlyPull(card)}
                      disabled={busyKey === card.key}
                      data-testid={`ac-earlypull-${card.store_id}-${card.delivery_date}`}
                      className="bg-sky-600 hover:bg-sky-700 text-white rounded-lg h-9"
                    >
                      🐟 事前取込（確定）
                    </Button>
                  ) : canApprove ? (
                    <Button
                      size="sm"
                      onClick={() => handleAll(card)}
                      disabled={busyKey === card.key}
                      data-testid={`ac-all-${card.store_id}-${card.delivery_date}`}
                      className="bg-slate-900 hover:bg-slate-800 text-white rounded-lg h-9"
                    >
                      <Zap className="h-4 w-4 mr-1" />
                      まとめて対応
                    </Button>
                  ) : null}
                </div>
              </div>

              {/* 🟠 変更承認セクション */}
              {card.needApprove && (
                <div className="px-4 py-3 border-b border-slate-100 bg-amber-50/40">
                  <div className="text-xs font-semibold text-amber-700 mb-2 flex items-center gap-1">
                    <AlertTriangle className="h-3.5 w-3.5" /> 変更承認（締切後の変更申請）
                  </div>
                  <div className="space-y-3">
                    {card.batches.map((b) => (
                      <div key={b.id} className="rounded-lg border border-amber-200 bg-white overflow-hidden">
                        <div className="flex items-center justify-between gap-2 px-3 py-1.5 bg-amber-50 border-b border-amber-100">
                          <div className="text-xs text-slate-600">
                            申請 {new Date(b.created_at).toLocaleString("ja-JP")}
                            {b.reason ? ` ・理由: ${b.reason}` : ""}
                          </div>
                          {canApprove && (
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                onClick={() => handleApprove(b.id)}
                                disabled={busyKey === b.id}
                                data-testid={`ac-approve-${b.id}`}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-md h-7 text-xs px-2"
                              >
                                <Check className="h-3 w-3 mr-1" /> 承認
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleReject(b.id)}
                                disabled={busyKey === b.id}
                                data-testid={`ac-reject-${b.id}`}
                                className="text-red-600 border-red-300 hover:bg-red-50 rounded-md h-7 text-xs px-2"
                              >
                                <X className="h-3 w-3 mr-1" /> 却下
                              </Button>
                            </div>
                          )}
                        </div>
                        <div className="divide-y divide-slate-100">
                          {(b.items || []).map((it, i) => {
                            const info = actionLabel(it);
                            const st = it.status || "pending";
                            return (
                              <div key={i} className="flex items-center justify-between gap-2 px-3 py-1.5 text-sm">
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded border shrink-0 ${it.action === "modify" ? "bg-amber-100 text-amber-700 border-amber-200" : it.action === "add" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-red-100 text-red-700 border-red-200"}`}>
                                    {info.label}
                                  </span>
                                  <span className="truncate font-medium text-slate-700">{info.name}</span>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  <span className="font-mono text-xs text-slate-600">{info.change}</span>
                                  {st !== "pending" && (
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${st === "approved" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-red-100 text-red-700 border-red-200"}`}>
                                      {st === "approved" ? "承認済" : "却下"}
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 🐟 事前取込セクション */}
              {card.needEarlyPull && card.group && (
                <div className="px-4 py-3 border-b border-slate-100 bg-sky-50/50" data-testid={`ac-earlypull-section-${card.store_id}-${card.delivery_date}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-semibold text-sky-700 flex items-center gap-1">
                      🐟 事前取込（{card.group.item_count}件・魚類など締切前の先取込）
                    </div>
                    {canApprove && (
                      <Button
                        size="sm"
                        onClick={() => handleEarlyPull(card)}
                        disabled={busyKey === card.key}
                        data-testid={`ac-earlypull-btn-${card.store_id}-${card.delivery_date}`}
                        className="rounded-lg h-8 text-white bg-sky-600 hover:bg-sky-700"
                      >
                        <Check className="h-3.5 w-3.5 mr-1" />
                        デスクへ事前取込（確定）
                      </Button>
                    )}
                  </div>
                  <div className="rounded-lg border border-sky-100 overflow-hidden bg-white">
                    <table className="w-full text-sm">
                      <tbody>
                        {(card.group.items || []).map((it, i) => (
                          <tr key={i} className={`border-b border-slate-50 last:border-b-0 ${it.early_pull ? "bg-sky-50" : ""}`}>
                            <td className="px-3 py-1.5">
                              <span className="font-medium text-slate-700">{it.product_name}</span>
                              {it.early_pull && (
                                <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-sky-100 text-sky-800 border border-sky-300 font-bold">🐟 事前取込</span>
                              )}
                              {it.is_unlisted && (
                                <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200">リストなし</span>
                              )}
                            </td>
                            <td className="px-3 py-1.5 text-right font-mono whitespace-nowrap text-slate-700">
                              {fmtQty(it.quantity)} {it.unit || ""}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* 🔵 注文確定セクション */}
              {card.needConfirm && card.group && (
                <div className="px-4 py-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-semibold text-sky-700 flex items-center gap-1">
                      <PackageCheck className="h-3.5 w-3.5" /> 注文確定（{card.group.item_count}件）
                      {card.isChanged && (
                        <span className="text-red-600 ml-1">※確定後に内容が変わっています</span>
                      )}
                    </div>
                    {canApprove && (
                      <Button
                        size="sm"
                        onClick={() => handleConfirm(card)}
                        disabled={busyKey === card.key}
                        data-testid={`ac-confirm-${card.store_id}-${card.delivery_date}`}
                        className={`rounded-lg h-8 text-white ${card.isChanged ? "bg-red-600 hover:bg-red-700" : "bg-sky-600 hover:bg-sky-700"}`}
                      >
                        <Check className="h-3.5 w-3.5 mr-1" />
                        {card.isChanged ? "再確定する" : "確定する"}
                      </Button>
                    )}
                  </div>
                  <div className="rounded-lg border border-slate-100 overflow-hidden">
                    <table className="w-full text-sm">
                      <tbody>
                        {(card.group.items || []).map((it, i) => (
                          <tr key={i} className="border-b border-slate-50 last:border-b-0">
                            <td className="px-3 py-1.5">
                              <span className="font-medium text-slate-700">{it.product_name}</span>
                              {it.is_unlisted && (
                                <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200">リストなし</span>
                              )}
                            </td>
                            <td className="px-3 py-1.5 text-right font-mono whitespace-nowrap">
                              {it.changed_from ? (
                                <span>
                                  <span className="text-slate-400 line-through mr-1">
                                    {fmtQty(it.changed_from.prev_quantity)} {it.changed_from.prev_unit || ""}
                                  </span>
                                  <span className="text-red-600 font-semibold">
                                    {fmtQty(it.quantity)} {it.unit || ""}
                                  </span>
                                </span>
                              ) : (
                                <span className="text-slate-700">{fmtQty(it.quantity)} {it.unit || ""}</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
